(() => {
  if (!customElements.get('fellinora-product-tabs')) {
    class FellinoraProductTabs extends HTMLElement {
      connectedCallback() {
        if (this.dataset.tabsInitialized === 'true') return;
        this.dataset.tabsInitialized = 'true';

        this.tabs = Array.from(this.querySelectorAll('[data-fdc-tab]'));
        this.panels = Array.from(this.querySelectorAll('[data-fdc-panel]'));
        if (!this.tabs.length || !this.panels.length) return;

        this.tabs.forEach((tab, index) => {
          tab.addEventListener('click', () => this.activate(tab, false));
          tab.addEventListener('keydown', (event) => this.handleKeydown(event, index));
        });

        const activeTab = this.tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || this.tabs[0];
        this.activate(activeTab, false);
      }

      activate(activeTab, moveFocus) {
        const activeName = activeTab.dataset.fdcTab;

        this.tabs.forEach((tab) => {
          const isActive = tab === activeTab;
          tab.classList.toggle('is-active', isActive);
          tab.setAttribute('aria-selected', String(isActive));
          tab.tabIndex = isActive ? 0 : -1;
        });

        this.panels.forEach((panel) => {
          panel.hidden = panel.dataset.fdcPanel !== activeName;
        });

        if (moveFocus) activeTab.focus();
      }

      handleKeydown(event, index) {
        let targetIndex = null;

        if (event.key === 'ArrowRight') targetIndex = (index + 1) % this.tabs.length;
        if (event.key === 'ArrowLeft') targetIndex = (index - 1 + this.tabs.length) % this.tabs.length;
        if (event.key === 'Home') targetIndex = 0;
        if (event.key === 'End') targetIndex = this.tabs.length - 1;
        if (targetIndex === null) return;

        event.preventDefault();
        this.activate(this.tabs[targetIndex], true);
      }
    }

    customElements.define('fellinora-product-tabs', FellinoraProductTabs);
  }

  if (customElements.get('fellinora-dosage-calculator')) return;

  class FellinoraDosageCalculator extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.range = this.querySelector('[data-fdc-range]');
      this.weightInput = this.querySelector('[data-fdc-weight]');
      this.result = this.querySelector('[data-fdc-result]');
      this.priceResult = this.querySelector('[data-fdc-price]');

      if (!this.range || !this.weightInput || !this.result || !this.priceResult) return;

      this.minimum = Number.parseFloat(this.dataset.minWeight) || 1;
      this.maximum = Number.parseFloat(this.dataset.maxWeight) || 9;
      this.step = Number.parseFloat(this.dataset.step) || 0.01;
      this.factor = Number.parseFloat(this.dataset.factor) || 0.2;
      this.packagePrice = this.parseInput(this.dataset.packagePrice) || 17.99;
      this.packageWeight = Number.parseFloat(this.dataset.packageWeight) || 100;
      this.locale = this.dataset.locale || 'de';

      this.numberFormatter = new Intl.NumberFormat(this.locale, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
      this.currencyFormatter = new Intl.NumberFormat(this.locale, {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

      this.range.addEventListener('input', () => {
        this.update(Number.parseFloat(this.range.value), true);
      });

      this.weightInput.addEventListener('input', () => {
        const value = this.parseInput(this.weightInput.value);
        if (Number.isFinite(value)) this.update(value, false);
      });

      this.weightInput.addEventListener('change', () => {
        const value = this.parseInput(this.weightInput.value);
        this.update(Number.isFinite(value) ? value : 4, true);
      });

      this.update(Number.parseFloat(this.range.value) || 4, true);
    }

    parseInput(value) {
      return Number.parseFloat(String(value).trim().replace(',', '.'));
    }

    clamp(value) {
      return Math.min(this.maximum, Math.max(this.minimum, value));
    }

    update(rawValue, normalizeInput) {
      const weight = this.clamp(rawValue);
      const dose = weight * this.factor;
      const dailyPrice = dose * (this.packagePrice / this.packageWeight);
      const progress = ((weight - this.minimum) / (this.maximum - this.minimum)) * 100;

      this.range.value = String(weight);
      this.range.style.setProperty('--fdc-range-progress', `${progress}%`);
      this.result.textContent = this.numberFormatter.format(dose);
      this.priceResult.textContent = this.currencyFormatter.format(dailyPrice);

      if (normalizeInput) {
        this.weightInput.value = this.numberFormatter.format(weight);
      }
    }
  }

  customElements.define('fellinora-dosage-calculator', FellinoraDosageCalculator);
})();
